import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface TermsOfServiceViewProps {
  open: boolean;
  onClose: () => void;
}

export function TermsOfServiceView({ open, onClose }: TermsOfServiceViewProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-white flex flex-col"
        >
          <div className="flex items-center justify-between p-6 border-b border-stone-100">
            <h1 className="text-xl font-extrabold tracking-tight text-stone-900">Conditions générales d'utilisation</h1>
            <button onClick={onClose} className="p-2 bg-stone-100 rounded-full text-stone-500 hover:text-stone-900 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto">
            <div className="prose prose-stone prose-sm max-w-none space-y-6 text-stone-700 text-sm leading-relaxed pb-24">
              <p className="text-xs text-stone-400">Dernière mise à jour : 11 mars 2026</p>

              <h2 className="text-base font-bold text-stone-900">1. Objet</h2>
              <p>Les présentes conditions générales d'utilisation (CGU) régissent l'accès et l'utilisation de l'application Atilum, un service d'optimisation de la fertilité basé sur l'analyse du mode de vie.</p>

              <h2 className="text-base font-bold text-stone-900">2. Acceptation</h2>
              <p>En créant un compte et en utilisant le service, vous acceptez sans réserve les présentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser le service.</p>

              <h2 className="text-base font-bold text-stone-900">3. Description du service</h2>
              <p>Atilum propose :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Un questionnaire d'évaluation du mode de vie et de la fertilité.</li>
                <li>Un score de fertilité personnalisé.</li>
                <li>Des recommandations d'habitudes à adopter.</li>
                <li>Un suivi quotidien (fonctionnalité Premium).</li>
              </ul>
              <p><strong>Atilum ne constitue pas un avis médical.</strong> Les informations fournies sont à titre informatif et ne remplacent en aucun cas une consultation médicale.</p>

              <h2 className="text-base font-bold text-stone-900">4. Abonnement et facturation</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>L'abonnement Premium est géré via Paddle, notre partenaire de facturation.</li>
                <li>L'abonnement est renouvelé automatiquement à chaque période (mensuelle ou annuelle).</li>
                <li>Vous pouvez annuler à tout moment depuis votre compte. L'accès Premium reste actif jusqu'à la fin de la période payée.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">5. Droit de rétractation</h2>
              <p>Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux contenus numériques fournis immédiatement après l'achat, ce que vous acceptez lors de la souscription.</p>

              <h2 className="text-base font-bold text-stone-900">6. Responsabilité</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Atilum fournit des recommandations générales basées sur des données scientifiques publiées.</li>
                <li>Atilum ne garantit aucun résultat en matière de fertilité.</li>
                <li>L'utilisateur est responsable de l'exactitude des informations fournies dans le questionnaire.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">7. Compte utilisateur</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Vous êtes responsable de la confidentialité de vos identifiants.</li>
                <li>Vous pouvez supprimer votre compte à tout moment. Cette action est irréversible et entraîne la suppression de toutes vos données.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">8. Propriété intellectuelle</h2>
              <p>L'ensemble du contenu de l'application (textes, design, code, marque) est la propriété exclusive d'Atilum SAS. Toute reproduction est interdite sans autorisation préalable.</p>

              <h2 className="text-base font-bold text-stone-900">9. Modification des CGU</h2>
              <p>Atilum se réserve le droit de modifier les présentes CGU. Les utilisateurs seront informés de toute modification significative par email.</p>

              <h2 className="text-base font-bold text-stone-900">10. Droit applicable</h2>
              <p>Les présentes CGU sont soumises au droit français. Tout litige sera soumis aux tribunaux compétents de Paris.</p>

              <h2 className="text-base font-bold text-stone-900">11. Contact</h2>
              <p>Pour toute question, contactez-nous à <strong>contact@atilum.com</strong>.</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
